import React from "react";
import {
  useGetProductByIdQuery,
  useGetProductVariationsQuery,
} from "../../api/productApi";
import { useProductVariationSelector } from "../../hooks";
import ProductModal from "./ProductModal";
import type { TransformedVariation } from "../../types/productApiTypes";
import type { RTKQueryError } from "../../../../../common/components/RTKQErrorCard";

// Define props interface
interface ProductModalContainerProps {
  productId: number;
  isOpen: boolean;
  onClose: () => void;
}

const ProductModalContainer: React.FC<ProductModalContainerProps> = ({
  productId,
  isOpen,
  onClose,
}) => {
  // Fetch product data only when modal is open and we have a productId
  const {
    data: product, // This is of type TransformedProduct | null | undefined
    isLoading: productLoading,
    isError: productError,
    error: productErrorDetails,
  } = useGetProductByIdQuery(productId, {
    skip: !isOpen || !productId,
  });

  // Fetch variations if needed
  const {
    data: variations = [] as TransformedVariation[],
    isLoading: variationsLoading,
    isError: variationsError,
    error: variationsErrorDetails,
  } = useGetProductVariationsQuery(product?.id ?? 0, {
    skip:
      !product?.id ||
      product?.type !== "variable" ||
      !product?.enrichedVariations?.length ||
      !isOpen,
  });

  // Use the variation selector hook with URL sync disabled
  const { selectedAttributeSlugs, selectedVariation, handleAttributeSelect } =
    useProductVariationSelector(product, variations, false);

  return (
    <ProductModal
      isOpen={isOpen}
      onClose={onClose}
      product={product}
      productLoading={productLoading}
      productError={productError}
      productErrorDetails={productErrorDetails as RTKQueryError | null}
      variationsLoading={variationsLoading}
      variationsError={variationsError}
      variationsErrorDetails={variationsErrorDetails as RTKQueryError | null}
      selectedAttributeSlugs={selectedAttributeSlugs}
      selectedVariation={selectedVariation}
      handleAttributeSelect={handleAttributeSelect}
    />
  );
};

export default ProductModalContainer;
